import React, { useEffect, useRef } from "react";
import styles from "./styles.module.scss";
import Image from "@/components/UI/Image";
import LazyLoader from "@/components/hoc/LazyLoader";
import Head from "next/head";
import SchemaLoader from "@/components/hoc/SchemaLoader";
import { WebsiteSchema } from "@/constants/schema";
import Config from "../../../config";
import { getStaticPath } from "../../../utils";
import { useRouter } from "next/router";
import WideBanner from "@/components/hoc/WideBanner";
import Slider from "react-slick";
import { isMobile } from "react-device-detect";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Form from "@/components/b2b-landingpage/Form";
import {
  b2bBreadCrumbSchema,
  b2bOrganizationSchema,
  banners
} from "@/constants/b2b";
import { cleverTapTrigger } from "../../../tracking/segment";
import page_data from "../../../data/page_data";
import FeatureImageBanner from "../../../components/common/FeatureImageBanner";
import FeatureTwoRowComponent from "../../../components/common/FeatureTwoRowComponent";
import Section from "../../../components/common/Section";
import featureStyles from "../../../styles/modules/common/features.module.scss"

const Fetcher = async () => {
  const data = {
    meta_tag_data: page_data["landingpage/b2b"]?.meta_tag || [],
    page_meta: page_data["landingpage/b2b"]?.page_meta || {},
  };

  return data;
};

export async function getServerSideProps() {
  const data = await Fetcher();
  return {
    props: {
      ...data,
      pageName: "b2b-landingpage",
    },
  };
}

const PrevArrow = (props) => {
  return (
    <a
      className={`${styles._arrow} ${styles._arrow_prev}`}
      href="#"
      onClick={props.onClick}
    ></a>
  );
};

const NextArrow = (props) => {
  return (
    <a
      className={`${styles._arrow} ${styles._arrow_next}`}
      href="#"
      onClick={props.onClick}
    ></a>
  );
};

const settings = {
  dots: false,
  infinite: true,
  arrows: true,
  slidesToShow: 3,
  slidesToScroll: 1,
  nextArrow: <NextArrow />,
  prevArrow: <PrevArrow />,
  responsive: [
    {
      breakpoint: 992,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1,
      },
    },
    {
      breakpoint: 768,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1,
        rows: 1,
        autoplay: true,
        autoplaySpeed: 3000,
        arrows: false,
        dots: true,
        infinite: true,
      },
    },
    {
      breakpoint: 576,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
        rows: 1,
        autoplay: true,
        autoplaySpeed: 3000,
        arrows: false,
        dots: true,
        infinite: true,
      },
    },
  ],
};

const B2BLandingPage = (props: any) => {
  const router = useRouter();
  const BASE_URL = Config.BASE_URL;
  const OG_IMAGE = getStaticPath("/static/images/why-onsitego.svg");
  const slideRef = useRef<any>(0);
  const contactRef = useRef<any>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleOnClickContactUs = () => {
    if (contactRef.current) {
      contactRef.current.scrollIntoView({ behavior: "smooth" });
    }
    cleverTapTrigger("Contact us Now Clicked", {});
  };

  const cached_props: any = props || {};
  const features: any = cached_props?.page_meta?.features || {};

  return (
    <>
      <Head>
        <title>{cached_props?.meta_tag_data?.tag_title}</title>
        <link rel="icon" href="/favicon.ico" />
        <meta
          name="description"
          content={cached_props?.meta_tag_data?.tag_description}
        ></meta>
        <meta
          property="og:title"
          content={cached_props?.meta_tag_data?.tag_title}
        />
        <meta
          property="og:description"
          content={cached_props?.meta_tag_data?.tag_description}
        />
        <meta property="og:url" content={`${BASE_URL}${router.asPath}`} />
        <meta property="og:image" content={OG_IMAGE} />
        <link rel="canonical" href={`${BASE_URL}${router.asPath}`}></link>
        <SchemaLoader schema={WebsiteSchema} />
        <SchemaLoader schema={b2bBreadCrumbSchema} />
        <SchemaLoader schema={b2bOrganizationSchema} />
      </Head>
      <div className={styles.bannerSection}>
        <WideBanner {...banners.banner1} />
        <div className={styles.subTitle}>
          <p>
            {cached_props.page_meta.page_title}
            <span>{cached_props.page_meta.page_sub_title}</span>
          </p>
        </div>
      </div>
      <LazyLoader>
        <div className="odd-even-start-with-gray">
          <FeatureImageBanner
            title={features.what_do_you_get?.title || ""}
            withSubtitle={false}
            features={features?.what_do_you_get?.features || ""}
            className={`${styles.features}`}
            imageSizeClass={featureStyles.amc__circle_image}
            isRoundShape
          />
          <section
            className={`${styles.bannerSection} ${styles.banner2}`}
            onClick={handleOnClickContactUs}
          >
            <WideBanner {...banners.banner2} />
          </section>
          <FeatureTwoRowComponent
            withSubtitle={false}
            features={features.twoRowFeatures.rowFeatures || []}
            title={features.twoRowFeatures.title || ""}
          />
          <FeatureImageBanner
            title={features.why_choose_onsitego?.title || ""}
            withSubtitle={false}
            features={features?.why_choose_onsitego?.features || ""}
            className={`${styles.companyStats}`}
            imageSizeClass={featureStyles.amc__circle_image}
            isRoundShape
          />
          <Section
            title="Industries We Service"
            className={styles.IndustriesSlider}
          >
            <div className={`container`}>
              <Slider
                className={styles.slick_slider}
                {...settings}
                ref={slideRef}
              >
                {features?.industries_we_service.map((industry, index) => {
                  return (
                    <div
                      className={styles.industryName}
                      key={index}
                      is-even={index % 2 == 0 ? "true" : "false"}
                    >
                      {industry}
                    </div>
                  );
                })}
              </Slider>
            </div>
          </Section>
          <section className={styles.ContactUs} ref={contactRef}>
            <div className={styles.contactUsForm}>
              <Form />
            </div>
            <div className={styles.contactUsImg}></div>
          </section>
        </div>
      </LazyLoader>
    </>
  );
};

export default B2BLandingPage;

